import { Delete } from "lucide-react";

/**
 * The numeric keypad a driver signs in on.
 *
 * Its own component, and its own keys rather than an `<input type="number">`,
 * because of where it is used: a tablet clamped to a dashboard, operated with
 * one hand, often in gloves, sometimes in the dark. A text field there summons
 * the OS keyboard — which on a locked-down kiosk tablet may not appear at all,
 * and which on the ones where it does covers the screen and offers autocorrect
 * on a secret.
 *
 * So the targets are deliberately large and the layout is deliberately the one
 * on every phone and every gate keypad in the world: 1-2-3 across the top,
 * zero at the bottom. Anything cleverer would have to be learned.
 *
 * The entered digits are shown as filled dots, never as numbers. A PIN typed in
 * a cab is typed in front of a windscreen, and the person behind the truck can
 * read a 42 mm digit from further away than anybody expects.
 */

interface PinKeypadProps {
  /** How many digits have been entered — drives the dots, not the value. */
  value: string;
  length: number;
  disabled?: boolean;
  onChange: (next: string) => void;
  /** Fired when the last digit lands, so there is no Enter key to hunt for. */
  onComplete: (pin: string) => void;
  labels: { clear: string; delete: string };
}

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

export function PinKeypad({
  value,
  length,
  disabled = false,
  onChange,
  onComplete,
  labels,
}: PinKeypadProps) {
  const press = (digit: string) => {
    if (disabled || value.length >= length) return;

    const next = value + digit;
    onChange(next);

    /**
     * Submitting on the last digit rather than on a separate button.
     *
     * The PIN is a fixed length, so the moment it is complete there is exactly
     * one thing the driver can mean. An extra tap on a confirm key would be a
     * tap that adds nothing and that somebody standing at a gate in the rain has
     * to find.
     */
    if (next.length === length) onComplete(next);
  };

  return (
    <div className="mx-auto w-full max-w-xs">
      {/* What has been entered, as dots. Never the digits themselves. */}
      <div
        className="mb-8 flex items-center justify-center gap-3"
        role="status"
        aria-label={`${value.length} of ${length} digits entered`}
      >
        {Array.from({ length }).map((_, index) => (
          <span
            key={index}
            className={`h-4 w-4 rounded-full transition-colors ${
              index < value.length
                ? "bg-[#0B0F0E]"
                : "border-2 border-[#D4D2CA] bg-transparent"
            }`}
          />
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3">
        {KEYS.map((digit) => (
          <button
            key={digit}
            type="button"
            disabled={disabled}
            onClick={() => press(digit)}
            className="h-16 rounded-2xl bg-white text-2xl font-semibold text-[#0B0F0E] shadow-sm transition-colors active:bg-[#F0EEE8] disabled:opacity-40 sm:h-20"
          >
            {digit}
          </button>
        ))}

        <button
          type="button"
          disabled={disabled || !value}
          onClick={() => onChange("")}
          className="h-16 rounded-2xl text-sm font-medium text-[#8A9590] transition-colors active:bg-[#F0EEE8] disabled:opacity-30 sm:h-20"
        >
          {labels.clear}
        </button>

        <button
          type="button"
          disabled={disabled}
          onClick={() => press("0")}
          className="h-16 rounded-2xl bg-white text-2xl font-semibold text-[#0B0F0E] shadow-sm transition-colors active:bg-[#F0EEE8] disabled:opacity-40 sm:h-20"
        >
          0
        </button>

        <button
          type="button"
          disabled={disabled || !value}
          aria-label={labels.delete}
          onClick={() => onChange(value.slice(0, -1))}
          className="flex h-16 items-center justify-center rounded-2xl text-[#8A9590] transition-colors active:bg-[#F0EEE8] disabled:opacity-30 sm:h-20"
        >
          <Delete className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
}
